'use client'

import { useQuery } from '@apollo/client'
import { usePathname } from 'next/navigation'
import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'

import { ANALYTICS_EVENTS } from '../constants/analytics'
import { MY_DETAILS } from '../data/queries/user'
import { useSelectedWorkspaceStore } from '../stores/auth-store'
import type { AnalyticsContextProps } from '../types/analytics'
import type { MeReturnType } from '../types/user'
import {
  ANALYTICS_READY_EVENT,
  captureEvent,
  identify,
  identifyWorkspace,
  isAnalyticsReady,
  reset,
  setUserProperty
} from '../utils/analytics-service'
import { clearAnalyticsSyncState, readAnalyticsSyncState, writeAnalyticsSyncState } from '../utils/analytics-sync-state'
import { isLightThemeRoute } from '../utils/light-theme-routes'
import { TOKEN_UPDATED_EVENT, tokenUtils } from '../utils/token-utils'
import { createGenericContext } from './generic-context'

const [useAnalytics, AnalyticsContextProvider] = createGenericContext<AnalyticsContextProps>()

type TAnalyticsSurface = 'app' | 'embed' | 'public'

interface AnalyticsProviderProps {
  children: ReactNode
}

const isPublicAnalyticsRoute = (pathname: string | null): boolean => {
  if (!pathname) return false

  return /^\/(?:[^/]+\/)?public(?:\/|$)/.test(pathname)
}

const getAnalyticsSurface = (pathname: string | null): TAnalyticsSurface => {
  if (pathname && isLightThemeRoute(pathname)) return 'embed'
  if (isPublicAnalyticsRoute(pathname)) return 'public'
  return 'app'
}

const getLocalDateKey = (): string => {
  const today = new Date()
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const getMillisecondsUntilNextDay = (): number => {
  const now = new Date()
  const nextDay = new Date(now)
  nextDay.setHours(24, 0, 0, 0)
  return Math.max(nextDay.getTime() - now.getTime(), 1000)
}

const AnalyticsProvider = ({ children }: AnalyticsProviderProps) => {
  const pathname = usePathname()
  const [userID, setUserID] = useState<string | undefined>(() => tokenUtils.getUserID() || undefined)
  const [analyticsReady, setAnalyticsReady] = useState<boolean>(() => isAnalyticsReady())
  const [localDateKey, setLocalDateKey] = useState<string>(() => getLocalDateKey())
  const { selectedWorkspace } = useSelectedWorkspaceStore()
  const lastIdentifiedUserIDRef = useRef<string | undefined>(undefined)
  const lastActiveEventKeyRef = useRef<string | undefined>(undefined)
  const lastUserPropertiesRef = useRef<string | undefined>(undefined)
  const lastWorkspaceKeyRef = useRef<string | undefined>(undefined)
  const surface = getAnalyticsSurface(pathname)

  const { data } = useQuery<{ me: MeReturnType }>(MY_DETAILS, {
    fetchPolicy: 'cache-first',
    skip: !userID
  })

  const clearRuntimeSyncRefs = () => {
    lastIdentifiedUserIDRef.current = undefined
    lastActiveEventKeyRef.current = undefined
    lastUserPropertiesRef.current = undefined
    lastWorkspaceKeyRef.current = undefined
  }

  const clearAnalyticsSync = () => {
    clearAnalyticsSyncState()
    clearRuntimeSyncRefs()
  }

  useEffect(() => {
    const syncUserID = () => {
      setUserID(tokenUtils.getUserID() || undefined)
    }

    syncUserID()

    const handleTokenUpdated = (event: Event) => {
      const tokenEvent = event as CustomEvent<{ tokenType?: string }>
      if (!tokenEvent.detail?.tokenType || tokenEvent.detail.tokenType === 'Access-Token') {
        syncUserID()
      }
    }

    window.addEventListener(TOKEN_UPDATED_EVENT, handleTokenUpdated as EventListener)
    return () => {
      window.removeEventListener(TOKEN_UPDATED_EVENT, handleTokenUpdated as EventListener)
    }
  }, [])

  useEffect(() => {
    if (analyticsReady) return

    const handleAnalyticsReady = () => {
      setAnalyticsReady(true)
    }

    if (isAnalyticsReady()) {
      handleAnalyticsReady()
      return
    }

    window.addEventListener(ANALYTICS_READY_EVENT, handleAnalyticsReady)
    return () => {
      window.removeEventListener(ANALYTICS_READY_EVENT, handleAnalyticsReady)
    }
  }, [analyticsReady])

  useEffect(() => {
    const rolloverTimeout = window.setTimeout(() => {
      setLocalDateKey(getLocalDateKey())
    }, getMillisecondsUntilNextDay())

    return () => {
      window.clearTimeout(rolloverTimeout)
    }
  }, [localDateKey])

  useEffect(() => {
    if (!analyticsReady) return

    const syncState = readAnalyticsSyncState()

    if (!userID) {
      if (lastIdentifiedUserIDRef.current || syncState.identifiedUserID) {
        reset()
      }

      clearAnalyticsSync()
      return
    }

    if (lastIdentifiedUserIDRef.current === userID && syncState.identifiedUserID === userID) return

    if (syncState.identifiedUserID !== userID) {
      identify(userID)
      writeAnalyticsSyncState({ identifiedUserID: userID })
      lastActiveEventKeyRef.current = undefined
      lastUserPropertiesRef.current = undefined
      lastWorkspaceKeyRef.current = undefined
    }

    lastIdentifiedUserIDRef.current = userID
  }, [analyticsReady, userID])

  useEffect(() => {
    if (!analyticsReady || !userID || !data?.me) return

    const { name, email, phone } = data.me
    const userProperties = {
      user_email: email || '',
      user_name: name || '',
      user_phone: phone || ''
    }
    const userPropertiesKey = JSON.stringify(userProperties)
    const syncState = readAnalyticsSyncState()

    if (lastUserPropertiesRef.current === userPropertiesKey) return
    if (syncState.identifiedUserID === userID && syncState.userPropertiesHash === userPropertiesKey) {
      lastUserPropertiesRef.current = userPropertiesKey
      return
    }

    setUserProperty(userProperties)
    lastUserPropertiesRef.current = userPropertiesKey
    writeAnalyticsSyncState({
      ...syncState,
      identifiedUserID: userID,
      userPropertiesHash: userPropertiesKey
    })
  }, [analyticsReady, data?.me?.email, data?.me?.name, data?.me?.phone, userID])

  useEffect(() => {
    if (!analyticsReady || !userID || !selectedWorkspace?.workspace) return

    const { id, displayName, workspacename, isVerified } = selectedWorkspace.workspace
    const workspaceKey = JSON.stringify({
      displayName,
      id,
      isVerified: isVerified ?? false,
      workspacename
    })
    const syncState = readAnalyticsSyncState()

    if (lastWorkspaceKeyRef.current === workspaceKey) return
    if (syncState.identifiedUserID === userID && syncState.workspaceKey === workspaceKey) {
      lastWorkspaceKeyRef.current = workspaceKey
      return
    }

    identifyWorkspace({ displayName, id, isVerified: isVerified ?? false, workspacename })
    lastWorkspaceKeyRef.current = workspaceKey
    writeAnalyticsSyncState({
      ...syncState,
      identifiedUserID: userID,
      workspaceKey
    })
  }, [
    analyticsReady,
    selectedWorkspace?.workspace?.displayName,
    selectedWorkspace?.workspace?.id,
    selectedWorkspace?.workspace?.isVerified,
    selectedWorkspace?.workspace?.workspacename,
    userID
  ])

  useEffect(() => {
    if (!analyticsReady || !userID) return

    const requiresWorkspaceGrouping = surface === 'app'
    if (requiresWorkspaceGrouping && !selectedWorkspace?.workspace) return

    const activeEventKey = `${userID}:${localDateKey}:${surface}`
    const syncState = readAnalyticsSyncState()

    if (lastActiveEventKeyRef.current === activeEventKey) return
    if (syncState.identifiedUserID === userID && syncState.lastActiveEventKey === activeEventKey) {
      lastActiveEventKeyRef.current = activeEventKey
      return
    }

    captureEvent(ANALYTICS_EVENTS.usage.appActive, { surface })
    lastActiveEventKeyRef.current = activeEventKey
    writeAnalyticsSyncState({
      ...syncState,
      identifiedUserID: userID,
      lastActiveEventKey: activeEventKey
    })
  }, [analyticsReady, localDateKey, selectedWorkspace?.workspace, surface, userID])

  const value: AnalyticsContextProps = { captureEvent, identify, identifyWorkspace, setUserProperty }

  return <AnalyticsContextProvider value={value}>{children}</AnalyticsContextProvider>
}

export { AnalyticsProvider, getAnalyticsSurface, useAnalytics }
